// src/utils/leadTrackerPdf.js
// Month Lead Tracker PDF drawn as a REPLICA of the coach's workbook month
// tab (landscape): the "Your Numbers & Metrics" panel down the left exactly
// like columns A-C, and the 12-column lead list (E-P) with grid lines on the
// right. buildPdfModel is pure (unit tested); renderLeadTrackerPdf turns the
// model into bytes; download reuses impactScorecardPdf's downloadBytes.
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import {
  TRACKER_BUCKETS,
  UNASSIGNED_BUCKET,
  MONTH_LABELS,
  monthMetrics,
  yearMetrics,
} from "./leadTracker";
import { downloadBytes } from "./impactScorecardPdf";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June", "July",
  "August", "September", "October", "November", "December",
];
const INK = rgb(0.12, 0.16, 0.23);
const MUTED = rgb(0.42, 0.45, 0.5);
const GOOD = rgb(0.09, 0.5, 0.24);
const BAD = rgb(0.78, 0.16, 0.16);
const GRID = rgb(0.85, 0.87, 0.9);
const BAND = rgb(0.93, 0.94, 0.96);

// Helvetica throws on characters outside WinAnsi (emoji etc.) — strip them.
export function winAnsiSafe(s) {
  return String(s == null ? "" : s).replace(/[^\x20-\x7E -ÿ]/g, "");
}

const pctText = (v) => (v == null ? "" : `${Math.round(v * 100)}%`);
const overUnder = (n) => (n > 0 ? `+${n}` : `${n}`);
const fmtDate = (ms) => {
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}`;
};

// The workbook's E-P column headers, verbatim.
export const SHEET_COLUMNS = [
  "Date",
  "Name",
  "Buyer / Seller",
  "Call or Appt",
  "Appt to Contract",
  "Pre-Approved",
  "Month Closed",
  "Referral Source Name",
  "Source",
  "Phone",
  "Email",
  "Notes",
];

// Left panel rows mirroring the sheet's A-C columns. Each entry is either
// { section } (bold caps band) or { label, value, goal?, tone? }.
export function buildPanelRows(metrics, goals, year, monthLabel) {
  const tone = (n) => (n >= 0 ? "good" : "bad");
  const rows = [
    { section: "YOUR NUMBERS & METRICS", goalHeader: true },
    {
      label: "LEAD TO APPOINTMENT",
      value: pctText(metrics.leadToApptPct),
      goal: pctText(goals.leadToApptPct),
      tone:
        metrics.leadToApptPct == null
          ? null
          : tone(metrics.leadToApptPct - goals.leadToApptPct),
    },
    {
      label: "APPOINTMENT TO CONTRACT",
      value: pctText(metrics.apptToContractPct),
      goal: pctText(goals.apptToContractPct),
      tone:
        metrics.apptToContractPct == null
          ? null
          : tone(metrics.apptToContractPct - goals.apptToContractPct),
    },
    {
      label: "CONTRACT TO CLOSE",
      value: pctText(metrics.contractToClosePct),
      goal: pctText(goals.contractToClosePct),
      tone:
        metrics.contractToClosePct == null
          ? null
          : tone(metrics.contractToClosePct - goals.contractToClosePct),
    },
    { section: `${monthLabel} LEADS` },
    { label: `Monthly Leads Goal ${year}`, value: `${goals.monthlyLeadsGoal}` },
    { label: `Monthly Leads Actual ${year}`, value: `${metrics.leads}` },
    {
      label: "OVER/UNDER - LEAD GOAL",
      value: overUnder(metrics.leadsOverUnder),
      tone: tone(metrics.leadsOverUnder),
    },
    { section: "LEAD TO APPOINTMENT" },
    {
      label: `Lead to Appointment Goal ${year}`,
      value: `${Math.round(metrics.apptGoal)}`,
    },
    { label: `Lead to Appointment Actual ${year}`, value: `${metrics.appts}` },
    {
      label: "OVER/UNDER GOAL",
      value: overUnder(Math.round(metrics.apptsOverUnder)),
      tone: tone(metrics.apptsOverUnder),
    },
    { section: "APPOINTMENT TO CONTRACT" },
    { label: "Appointment to Contract", value: `${metrics.contracts}` },
    { label: "APPT TO CONTRACT %", value: pctText(metrics.apptToContractPct) },
    { section: "CLOSINGS" },
    { label: "Closings Goal", value: `${goals.closingsGoal}` },
    { label: "Closings Actual", value: `${metrics.closings}` },
    {
      label: "OVER/UNDER GOAL",
      value: overUnder(metrics.closingsOverUnder),
      tone: tone(metrics.closingsOverUnder),
    },
    { section: "LEAD ANALYSIS" },
    ...TRACKER_BUCKETS.map((b) => ({
      label: `${b.code} | ${b.label}`,
      value: `${metrics.bucketCounts[b.code] || 0}`,
    })),
  ];
  const un = metrics.bucketCounts[UNASSIGNED_BUCKET] || 0;
  if (un > 0) rows.push({ label: "Unassigned source", value: `${un}`, tone: "bad" });
  rows.push(
    {
      label: "TOTAL",
      value: `${Object.values(metrics.bucketCounts).reduce((a, n) => a + n, 0)}`,
    },
    { label: "Buyer", value: `${metrics.buyers}` },
    { label: "Seller", value: `${metrics.sellers}` },
    { label: "Pre-Approved", value: `${metrics.preApproved}` },
  );
  return rows;
}

export function buildPdfModel({ rows, metrics, goals, year, monthIndex, ownerName }) {
  const monthName = MONTH_NAMES[monthIndex];
  const monthLabel = MONTH_LABELS[monthIndex];
  const monthRows = rows
    .filter((r) => r.year === year && r.monthIndex === monthIndex)
    .sort((a, b) => a.createdAt - b.createdAt);
  const tableRows = monthRows.map((r) => [
    fmtDate(r.createdAt),
    r.name,
    r.leadType === "buyer" ? "Buyer" : "Seller",
    r.callOrAppt === "appt" ? "Appt" : "Call",
    r.contract ? "Yes" : "No",
    r.preApproved ? "Yes" : "",
    r.closedYear === year ? r.closedLabel : "",
    r.referredByName || "",
    r.bucket === UNASSIGNED_BUCKET ? "" : r.bucket,
    r.phone || "",
    r.email || "",
    r.notes || "",
  ]);
  return {
    title: `${monthName.toUpperCase()} ${year}`,
    subtitle: `Lead Tracker  |  ${ownerName}`,
    panelRows: buildPanelRows(metrics, goals, year, monthLabel),
    tableHeader: SHEET_COLUMNS,
    tableRows,
    monthLabel,
    monthName,
  };
}

// Year PDF: the Excel's shape in one document — every month's page plus the
// annual summary (Henry, 2026-09-01: the PDF should come in "the same form
// as an xcel downalds"; he chose separate Month and Year buttons).
export function buildYearPdfModel({ rows, goals, year, ownerName }) {
  const monthModels = MONTH_LABELS.map((_, mi) =>
    buildPdfModel({
      rows,
      metrics: monthMetrics(rows, year, mi, goals),
      goals,
      year,
      monthIndex: mi,
      ownerName,
    }),
  );
  const { months, totals } = yearMetrics(rows, year, goals);
  const header = [
    "Month", "Leads", "Appts", "Contracts", "Closings",
    "Lead to Appt", "Appt to Contract", "Contract to Close",
    "Buyers", "Sellers", "Pre-Approved",
  ];
  const annualRows = months.map((m) => [
    m.label, `${m.leads}`, `${m.appts}`, `${m.contracts}`, `${m.closings}`,
    pctText(m.leadToApptPct), pctText(m.apptToContractPct),
    pctText(m.contractToClosePct), `${m.buyers}`, `${m.sellers}`,
    `${m.preApproved}`,
  ]);
  const totalsRow = [
    "TOTAL", `${totals.leads}`, `${totals.appts}`, `${totals.contracts}`,
    `${totals.closings}`, "", "", "", `${totals.buyers}`, `${totals.sellers}`,
    `${totals.preApproved}`,
  ];
  return {
    title: `LEAD TRACKER ${year}`,
    subtitle: `Annual Metrics  |  ${ownerName}`,
    goalsLine:
      `Goals: ${goals.monthlyLeadsGoal} leads/mo  ·  ` +
      `${pctText(goals.leadToApptPct)} lead to appt  ·  ` +
      `${pctText(goals.apptToContractPct)} appt to contract  ·  ` +
      `${pctText(goals.contractToClosePct)} contract to close  ·  ` +
      `${goals.closingsGoal} closings/mo`,
    monthModels,
    annual: { header, rows: annualRows, totals: totalsRow },
  };
}

// Landscape letter. Left panel = sheet columns A-C; table = E-P.
const PAGE_W = 792;
const PAGE_H = 612;
const M = 24;
const PANEL_W = 200;
const TABLE_X = M + PANEL_W + 12;
// Column widths across 532pt, roughly proportional to the sheet's.
const COL_W = [42, 76, 32, 32, 36, 34, 34, 62, 28, 52, 68, 36];
const ROW_H = 11;
const TABLE_FONT = 6.3;

// Draw one month (panel + lead table, overflowing onto extra pages) into an
// existing document. Shared by the single-month and full-year renderers.
function drawMonthPages(doc, font, bold, model) {
  const colX = [];
  let acc = TABLE_X;
  COL_W.forEach((w) => {
    colX.push(acc);
    acc += w;
  });
  const tableRight = acc;

  const newPage = () => doc.addPage([PAGE_W, PAGE_H]);
  let page = newPage();

  const text = (t, x, y, opts = {}) => {
    page.drawText(winAnsiSafe(t), {
      x,
      y,
      size: opts.size ?? 7.2,
      font: opts.bold ? bold : font,
      color:
        opts.tone === "good"
          ? GOOD
          : opts.tone === "bad"
            ? BAD
            : opts.muted
              ? MUTED
              : INK,
    });
  };
  const clip = (t, w, size) => {
    let s = winAnsiSafe(t).replace(/\s+/g, " ").trim();
    while (s.length && font.widthOfTextAtSize(s, size) > w - 3) {
      s = s.slice(0, -1);
    }
    return s;
  };

  // ── Title band ──
  text(model.title, M, PAGE_H - M - 14, { size: 16, bold: true });
  text(model.subtitle, M, PAGE_H - M - 28, { size: 9, muted: true });
  const top = PAGE_H - M - 44;

  // ── Left panel (page 1 only) ──
  let py = top;
  model.panelRows.forEach((r) => {
    if (py < M + 8) return; // panel never overflows in practice (33 rows fit)
    if (r.section) {
      py -= 4;
      page.drawRectangle({
        x: M,
        y: py - 3,
        width: PANEL_W,
        height: 12,
        color: BAND,
      });
      text(r.section, M + 3, py, { size: 6.8, bold: true });
      if (r.goalHeader) text("Goal", M + PANEL_W - 24, py, { size: 6.8, bold: true, muted: true });
      py -= 12;
      return;
    }
    text(clip(r.label, PANEL_W - 60, 6.8), M + 3, py, { size: 6.8 });
    text(r.value ?? "", M + PANEL_W - 52, py, { size: 6.8, bold: true, tone: r.tone });
    if (r.goal != null) text(r.goal, M + PANEL_W - 24, py, { size: 6.8, muted: true });
    py -= 10.5;
  });
  page.drawLine({
    start: { x: M + PANEL_W + 6, y: top + 10 },
    end: { x: M + PANEL_W + 6, y: Math.min(py + 6, M) + 6 },
    thickness: 0.7,
    color: GRID,
  });

  // ── Table (continues onto extra pages, header repeated) ──
  // Long sheet headers ("Referral Source Name") wrap onto two lines, like
  // Excel renders them, instead of getting chopped.
  const headerLines = (h, w) => {
    if (bold.widthOfTextAtSize(h, 5.8) <= w - 3) return [h];
    // Break at spaces, and at hyphens for words like "Pre-Approved".
    const words = h.replace(/-(?=\S)/g, "- ").split(" ");
    const lines = ["", ""];
    let li = 0;
    words.forEach((word) => {
      const tryLine = lines[li] ? `${lines[li]} ${word}` : word;
      if (li === 0 && bold.widthOfTextAtSize(tryLine, 5.8) > w - 3) li = 1;
      lines[li] = lines[li] ? `${lines[li]} ${word}` : word;
    });
    return lines.filter(Boolean);
  };
  const drawHeader = (y) => {
    page.drawRectangle({
      x: TABLE_X - 2,
      y: y - 9,
      width: tableRight - TABLE_X + 2,
      height: 19,
      color: BAND,
    });
    model.tableHeader.forEach((h, i) => {
      const lines = headerLines(h, COL_W[i]);
      if (lines.length === 1) {
        text(lines[0], colX[i], y - 3, { size: 5.8, bold: true });
      } else {
        text(lines[0], colX[i], y + 1, { size: 5.8, bold: true });
        text(clip(lines[1], COL_W[i], 5.8), colX[i], y - 6, { size: 5.8, bold: true });
      }
    });
    return y - ROW_H - 7;
  };
  const drawGridRow = (y) => {
    page.drawLine({
      start: { x: TABLE_X - 2, y: y - 3 },
      end: { x: tableRight, y: y - 3 },
      thickness: 0.4,
      color: GRID,
    });
  };

  let ty = drawHeader(top);
  model.tableRows.forEach((cells) => {
    if (ty < M + 6) {
      page = newPage();
      text(`${model.title} (continued)`, M, PAGE_H - M - 12, { size: 10, bold: true });
      ty = drawHeader(PAGE_H - M - 30);
    }
    cells.forEach((c, i) => {
      text(clip(c, COL_W[i], TABLE_FONT), colX[i], ty, { size: TABLE_FONT });
    });
    drawGridRow(ty);
    ty -= ROW_H;
  });
  if (model.tableRows.length === 0) {
    text("No leads this month", TABLE_X, ty, { size: 8, muted: true });
  }
}

// The annual summary page: per-month counts + conversion rates, totals row,
// goals line — the PDF twin of the workbook's ANNUAL METRICS tab.
const ANNUAL_COL_W = [50, 48, 48, 60, 54, 68, 84, 88, 48, 48, 70];
function drawAnnualPage(doc, font, bold, model) {
  const page = doc.addPage([PAGE_W, PAGE_H]);
  const text = (t, x, y, opts = {}) => {
    page.drawText(winAnsiSafe(t), {
      x, y,
      size: opts.size ?? 8.5,
      font: opts.bold ? bold : font,
      color: opts.muted ? MUTED : INK,
    });
  };
  text(model.title, M, PAGE_H - M - 14, { size: 16, bold: true });
  text(model.subtitle, M, PAGE_H - M - 28, { size: 9, muted: true });
  text(model.goalsLine, M, PAGE_H - M - 41, { size: 8, muted: true });

  const colX = [];
  let acc = M;
  ANNUAL_COL_W.forEach((w) => {
    colX.push(acc);
    acc += w;
  });
  const right = acc;
  const rowH = 24;
  let y = PAGE_H - M - 66;

  page.drawRectangle({
    x: M - 2, y: y - 7, width: right - M + 4, height: 18, color: BAND,
  });
  model.annual.header.forEach((h, i) => text(h, colX[i], y - 2, { size: 8, bold: true }));
  y -= rowH;

  const drawRow = (cells, isTotal) => {
    cells.forEach((c, i) =>
      text(c, colX[i], y, { size: 8.5, bold: isTotal || i === 0 }),
    );
    page.drawLine({
      start: { x: M - 2, y: y - 8 },
      end: { x: right, y: y - 8 },
      thickness: 0.4,
      color: GRID,
    });
    y -= rowH;
  };
  model.annual.rows.forEach((cells) => drawRow(cells, false));
  drawRow(model.annual.totals, true);
}

export async function renderLeadTrackerPdf(model) {
  const doc = await PDFDocument.create();
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);
  drawMonthPages(doc, font, bold, model);
  return doc.save();
}

export async function renderLeadTrackerYearPdf(model) {
  const doc = await PDFDocument.create();
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);
  model.monthModels.forEach((m) => drawMonthPages(doc, font, bold, m));
  drawAnnualPage(doc, font, bold, model);
  return doc.save();
}

export async function downloadLeadTrackerPdf(model, year) {
  const bytes = await renderLeadTrackerPdf(model);
  downloadBytes(bytes, `Lead Tracker - ${model.monthName} ${year}.pdf`);
}

export async function downloadLeadTrackerYearPdf(model, year, ownerName) {
  const bytes = await renderLeadTrackerYearPdf(model);
  downloadBytes(bytes, `Lead Tracker ${year} - ${ownerName}.pdf`);
}
